import {
  ChangeEvent,
  FocusEvent,
  KeyboardEventHandler,
  forwardRef,
} from "react";

import { TextInput } from "chameleon/lib/components";
import HelpTooltip from "chameleon/lib/components/help-tooltip/help-tooltip";

import { inputWrapper } from "./cvv-input.css";

export interface CVVInputProps {
  /**
   * Aria Label for accessibility
   */
  ariaLabel: string;
  /**
   * Autocomplete tag
   */
  autocomplete?: string;
  /**
   * caption for the input
   */
  captionMessage?: string;
  /**
   * Error message for the input
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Info used for the input title's info icon
   */
  info?: string;
  /**
   * Is the input disabled
   */
  isDisabled?: boolean;
  /**
   * Is the input required?
   */
  isRequired?: boolean;
  /**
   * onBlur handler
   */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * onFocus handler
   */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * Placeholder for the input
   */
  placeholder?: string;
  /**
   * Title for the input
   */
  title?: string;
  /**
   * Max length for the input
   */
  maxLength?: number;
  /**
   * Value
   */
  value?: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Flag for hiding messaging in iframe wrapper
   */
  inIframe?: boolean;
  /**
   * Tooltip content text to explain cvv
   */
  translatedValues?: { tooltipContent: string; tooltipAriaLabel?: string };
  /**
   * onKeyUp = handle key up events
   */
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * onKeyDown = handle key down events
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * shouldMaskValue = true to mask the value
   */
  shouldMaskValue?: boolean;
}

/**
 * Primary CVVInput component. Accepts a forwarded Ref!
 */
export const CVVInput = forwardRef<HTMLInputElement, CVVInputProps>(
  (
    {
      ariaLabel,
      info,
      onFocus,
      onBlur,
      isDisabled = false,
      isRequired = false,
      maxLength,
      errorMessage,
      errorMessageKey,
      captionMessage,
      id,
      title,
      onChange,
      placeholder,
      value,
      tabIndex,
      inIframe = false,
      autocomplete = "cc-csc",
      translatedValues = {
        tooltipContent:
          "Security Code is a 3 or 4-digit code typically located on the back of the card.",
      },
      onKeyDown,
      onKeyUp,
      shouldMaskValue = false,
    }: CVVInputProps,
    ref
  ) => {
    return (
      <div className={inputWrapper}>
        <TextInput
          ariaLabel={ariaLabel}
          info={info}
          onFocus={onFocus}
          onBlur={onBlur}
          isDisabled={isDisabled}
          isRequired={isRequired}
          errorMessage={errorMessage}
          errorMessageKey={errorMessageKey}
          captionMessage={captionMessage}
          id={id}
          title={title}
          maxLength={maxLength}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
          ref={ref}
          tabIndex={tabIndex}
          inIframe={inIframe}
          OutsideRightElement={
            <HelpTooltip
              content={translatedValues.tooltipContent}
              tooltipAriaLabel={translatedValues.tooltipAriaLabel}
            />
          }
          autocomplete={autocomplete}
          inputMode={"numeric"}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          shouldMaskValue={shouldMaskValue}
        />
      </div>
    );
  }
);

CVVInput.displayName = "CVVInput";
